import React, { useState } from 'react';
import { Button, Form, Input, Tabs, message, Divider, Modal } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { firebase_api } from '../api/firebase-api';
import { auth_api } from '../api/auth-api';
import { useDispatch } from 'react-redux';
import { auth_actions } from '../redux/modules/auth';
import { useNavigate } from 'react-router-dom';
import LoadingSpin from '../components/Loading/LoadingSpin';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth as firebaseAuth } from '../firebase/config';
import { getDeviceToken, getDeviceInfo } from '../utils/device-token';

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onLogin = async (values) => {
    try {
      setLoading(true);
      
      // Lấy device token và device info
      console.log('[Frontend Login] Đang lấy device token...');
      const deviceToken = await getDeviceToken();
      const deviceInfo = getDeviceInfo();
      
      console.log('[Frontend Login] Device token result:', {
        hasDeviceToken: !!deviceToken,
        deviceTokenLength: deviceToken?.length || 0,
        deviceTokenPreview: deviceToken ? deviceToken.substring(0, 30) + '...' : 'null',
        deviceInfo: deviceInfo
      });
      
      if (!deviceToken) {
        console.warn('[Frontend Login] ⚠️  Device token là null! FCM token sẽ không được lưu vào database.');
        console.warn('[Frontend Login] Kiểm tra:');
        console.warn('  1. VAPID key đã được cấu hình trong .env?');
        console.warn('  2. Notification permission đã được cấp?');
        console.warn('  3. Service Worker đã được đăng ký?');
      }
      
      // Đăng nhập với Firebase (gửi kèm device token)
      const firebaseResponse = await firebase_api.login(
        values.email, 
        values.password,
        deviceToken,
        deviceInfo
      );
      const { idToken, refreshToken, localId, email: firebaseEmail } = firebaseResponse;
      
      console.log('Firebase login success:', { localId, email: firebaseEmail });
      
      // Lưu token vào localStorage
      window.localStorage.setItem("api-token", idToken);
      window.localStorage.setItem("api-refresh-token", refreshToken);
      
      // Đăng nhập vào backend (tự động tạo user trong database nếu chưa có)
      // Gọi API login của backend để lấy user data đầy đủ từ database
      try {
        const loginResponse = await auth_api.loginWithPassword({
          email: firebaseEmail || values.email,
          password: values.password,
          deviceToken,
          deviceInfo
        });
        
        console.log('[Frontend] Backend login response:', loginResponse);
        console.log('[Frontend] User data from backend:', loginResponse.user);
        console.log('[Frontend] User displayName:', loginResponse.user?.displayName);
        console.log('[Frontend] User photoURL:', loginResponse.user?.photoURL);
        console.log('[Frontend] User phoneNumber:', loginResponse.user?.phoneNumber);
        
        if (loginResponse.success && loginResponse.user) {
          // Lấy đầy đủ thông tin user từ database (bao gồm displayName, photoURL, phoneNumber)
          // Sử dụng trực tiếp user data từ backend, không normalize
          const userData = loginResponse.user;
          console.log('[Frontend] Dispatching user data (raw from backend):', userData);
          console.log('[Frontend] User data displayName:', userData.displayName);
          console.log('[Frontend] User data photoURL:', userData.photoURL);
          console.log('[Frontend] User data phoneNumber:', userData.phoneNumber);
          console.log('[Frontend] User data email:', userData.email);
          
          // Dispatch user data trực tiếp từ backend
          dispatch(auth_actions.set_user(userData));
          message.success('Đăng nhập thành công!');
          navigate('/');
          return;
        }
      } catch (loginErr) {
        console.error('Backend login error:', loginErr.response?.data || loginErr.message);
        
        // Nếu backend login thất bại, thử lấy user từ database
        try {
          const userResponse = await auth_api.getMe();
          if (userResponse.success && userResponse.user) {
            dispatch(auth_actions.set_user(userResponse.user));
            message.success('Đăng nhập thành công!');
            navigate('/');
            return;
          }
        } catch (getMeErr) {
          console.error('Get me error:', getMeErr);
        }
        
        // Nếu cả hai đều thất bại, vẫn cho phép đăng nhập với thông tin từ Firebase
        const tempUser = {
          firebaseUid: localId,
          email: firebaseEmail || values.email,
          displayName: '',
          role: 'user',
          status: 'active'
        };
        
        dispatch(auth_actions.set_user(tempUser));
        message.warning('Đăng nhập Firebase thành công. User sẽ được tạo trong database khi cần.');
        navigate('/');
        return;
      }
      
      throw new Error('Không thể lấy thông tin user');
    } catch (err) {
      console.error('Login error:', err);
      window.localStorage.removeItem("api-token");
      window.localStorage.removeItem("api-refresh-token");
      
      if (err.response?.data?.error?.message?.includes('INVALID_PASSWORD') || 
          err.response?.data?.error?.message?.includes('EMAIL_NOT_FOUND')) {
        message.error('Email hoặc mật khẩu không chính xác!');
      } else {
        message.error(err.response?.data?.message || err.message || 'Đăng nhập thất bại. Vui lòng thử lại!');
      }
    } finally {
      setLoading(false);
    }
  };

  const onLoginWithGoogle = async () => {
    try {
      setLoading(true);
      
      // Lấy device token và device info
      const deviceToken = await getDeviceToken();
      const deviceInfo = getDeviceInfo();
      
      // Đăng nhập với Google qua Firebase
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(firebaseAuth, provider);
      const user = result.user;
      
      // Lấy ID token (forceRefresh = false để tránh gọi accounts:lookup)
      const idToken = await user.getIdToken(false);
      const refreshToken = user.refreshToken;
      const uid = user.uid;
      const email = user.email;
      // Lấy thông tin từ Google account
      const displayName = user.displayName || user.email?.split('@')[0] || '';
      const photoURL = user.photoURL || '';
      
      console.log('Google login success:', { 
        uid, 
        email, 
        displayName: displayName || '(empty)', 
        photoURL: photoURL ? 'has photo' : 'no photo' 
      });
      
      // Lưu token vào localStorage
      window.localStorage.setItem("api-token", idToken);
      window.localStorage.setItem("api-refresh-token", refreshToken);
      
      // Đăng nhập vào backend (tự động tạo user trong database nếu chưa có)
      // Gửi kèm device token
      try {
        console.log('[Frontend] ========== CALLING BACKEND GOOGLE LOGIN ==========');
        console.log('[Frontend] API_BASE_URL:', API_BASE_URL);
        console.log('[Frontend] Request data:', {
          hasIdToken: !!idToken,
          hasEmail: !!email,
          hasUid: !!uid,
          email: email,
          uid: uid,
          displayName: displayName || '(empty)',
          photoURL: photoURL ? 'has photo' : 'no photo',
          hasDeviceToken: !!deviceToken
        });
        
        // Sử dụng axios trực tiếp (không qua interceptor) vì chưa có token
        const loginResponse = await axios.post(
          `${API_BASE_URL}/api/auth/google`,
          {
            idToken,
            email,
            uid,
            displayName,
            photoURL,
            deviceToken,
            deviceInfo
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        ).then(res => res.data);
        
        console.log('[Frontend] Backend login response:', loginResponse);
        
        if (loginResponse.success) {
          if (loginResponse.user) {
            // Đảm bảo displayName và photoURL từ Google được lưu vào user object
            const updatedUser = {
              ...loginResponse.user,
              displayName: loginResponse.user.displayName || displayName || '',
              photoURL: loginResponse.user.photoURL || photoURL || '',
              phoneNumber: loginResponse.user.phoneNumber || '',
              email: loginResponse.user.email || email || ''
            };
            console.log('Google login: Dispatching user data:', updatedUser);
            dispatch(auth_actions.set_user(updatedUser));
            message.success('Đăng nhập bằng Google thành công!');
            navigate('/');
            return;
          } else {
            // Nếu không có user, có thể là lỗi từ server
            console.error('Google login response không có user:', loginResponse);
            if (loginResponse.error) {
              message.error('Đăng nhập Google thất bại: ' + (loginResponse.message || loginResponse.error));
            } else {
              message.error('Đăng nhập Google thành công nhưng không thể lấy thông tin user. Vui lòng thử lại.');
            }
            // Vẫn cho phép đăng nhập với thông tin tạm thời
            const tempUser = {
              firebaseUid: uid,
              email: email,
              displayName: displayName || '',
              photoURL: photoURL || '',
              role: 'user',
              status: 'active'
            };
            dispatch(auth_actions.set_user(tempUser));
            navigate('/');
            return;
          }
        } else {
          // Response không success
          console.error('[Frontend] Backend response không success:', loginResponse);
          message.error(loginResponse.message || 'Đăng nhập Google thất bại');
        }
      } catch (loginErr) {
        console.error('[Frontend] ========== BACKEND LOGIN ERROR ==========');
        console.error('[Frontend] Error message:', loginErr.message);
        console.error('[Frontend] Error response:', loginErr.response?.data);
        console.error('[Frontend] Error status:', loginErr.response?.status);
        console.error('[Frontend] Error config:', {
          url: loginErr.config?.url,
          method: loginErr.config?.method,
          data: loginErr.config?.data ? JSON.parse(loginErr.config.data) : null
        });
        
        // Nếu backend login thất bại, thử lấy user từ database
        try {
          const userResponse = await auth_api.getMe();
          if (userResponse.success && userResponse.user) {
            dispatch(auth_actions.set_user(userResponse.user));
            message.success('Đăng nhập bằng Google thành công!');
            navigate('/');
            return;
          }
        } catch (getMeErr) {
          console.error('Get me error:', getMeErr);
        }
        
        // Nếu cả hai đều thất bại, vẫn cho phép đăng nhập với thông tin từ Google
        const tempUser = {
          firebaseUid: uid,
          email: email,
          displayName: displayName,
          photoURL: photoURL,
          role: 'user',
          status: 'active'
        };
        
        dispatch(auth_actions.set_user(tempUser));
        message.warning('Đăng nhập Google thành công. User sẽ được tạo trong database khi cần.');
        navigate('/');
        return;
      }
      
      throw new Error('Không thể lấy thông tin user');
    } catch (err) {
      console.error('Google login error:', err);
      window.localStorage.removeItem("api-token");
      window.localStorage.removeItem("api-refresh-token");
      
      if (err.code === 'auth/popup-closed-by-user') {
        message.warning('Đăng nhập bị hủy. Vui lòng thử lại!');
      } else if (err.code === 'auth/popup-blocked') {
        message.error('Popup bị chặn. Vui lòng cho phép popup và thử lại!');
      } else if (err.code === 'auth/unauthorized-domain') {
        message.error(
          'Domain hiện tại chưa được phép đăng nhập Google. Thêm domain vào Firebase Console → Authentication → Settings → Authorized domains (ví dụ: giabinhtech.com).'
        );
      } else if (err.code === 'auth/operation-not-allowed') {
        message.error('Google Sign-In chưa được bật trong Firebase Console → Authentication → Sign-in method.');
      } else {
        message.error(err.message || 'Đăng nhập bằng Google thất bại. Vui lòng thử lại!');
      }
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (values) => {
    try {
      if (values.password !== values.confirmPassword) {
        message.error('Mật khẩu xác nhận không khớp!');
        return;
      }

      setLoading(true);
      
      // Lấy device token và device info
      const deviceToken = await getDeviceToken();
      const deviceInfo = getDeviceInfo();
      
      // Đăng ký với Firebase (gửi kèm device token)
      let firebaseResponse;
      try {
        firebaseResponse = await firebase_api.register(
          values.email, 
          values.password,
          deviceToken,
          deviceInfo
        );
      } catch (firebaseErr) {
        console.error('Firebase register error:', firebaseErr);
        if (firebaseErr.response?.data?.error?.message?.includes('EMAIL_EXISTS')) {
          message.error('Email này đã được sử dụng!');
          return;
        } else if (firebaseErr.response?.data?.error?.message === 'CONFIGURATION_NOT_FOUND') {
          message.error('Firebase chưa được cấu hình đúng. Vui lòng kiểm tra lại API key.');
          return;
        } else {
          throw firebaseErr;
        }
      }
      
      const { idToken, refreshToken, localId, email: firebaseEmail } = firebaseResponse;
      
      console.log('Firebase register success:', { localId, email: firebaseEmail });
      
      // Lưu token vào localStorage
      window.localStorage.setItem("api-token", idToken);
      window.localStorage.setItem("api-refresh-token", refreshToken);
      
      // Đăng nhập vào backend (tự động tạo user trong database)
      // Gửi cả token và thông tin user trong body để fallback nếu cần
      try {
        console.log('Calling login endpoint with fallback data...');
        const loginResponse = await auth_api.login({
          email: firebaseEmail || values.email,
          uid: localId,
          displayName: values.displayName || '',
          photoURL: ''
        });
        
        console.log('Login response:', loginResponse);
        
        if (loginResponse.success && loginResponse.user) {
          dispatch(auth_actions.set_user(loginResponse.user));
          message.success('Đăng ký thành công!');
          navigate('/');
          return;
        } else {
          throw new Error('Login response không thành công');
        }
      } catch (loginErr) {
        console.error('Login error:', loginErr.response?.data || loginErr.message);
        
        // Nếu login thất bại, vẫn coi như đăng ký thành công với Firebase
        // User đã được tạo trong Firebase, chỉ cần tạo trong database sau
        // Tạo user object tạm thời từ Firebase response
        const tempUser = {
          firebaseUid: localId,
          email: firebaseEmail || values.email,
          displayName: values.displayName || '',
          role: 'user',
          status: 'active'
        };
        
        dispatch(auth_actions.set_user(tempUser));
        message.warning('Đăng ký Firebase thành công. User sẽ được tạo trong database khi đăng nhập lại.');
        navigate('/');
        return;
      }
    } catch (err) {
      console.error('Register error:', err);
      window.localStorage.removeItem("api-token");
      window.localStorage.removeItem("api-refresh-token");
      
      if (err.response?.data?.error?.message?.includes('EMAIL_EXISTS')) {
        message.error('Email này đã được sử dụng!');
      } else if (err.response?.data?.error === 'FIREBASE_NOT_INITIALIZED') {
        message.error('Firebase chưa được cấu hình trên server. Vui lòng liên hệ admin.');
      } else {
        const errorMsg = err.response?.data?.message || err.message || 'Đăng ký thất bại. Vui lòng thử lại!';
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="flex items-center justify-center" 
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: "#ffffff"
      }}
    >
      <div style={{ transform: "translateY(-60px)", position: "relative", width: '100%', maxWidth: 450 }}>
        {loading && (
          <div style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 3,
            background: "rgba(255,255,255,0.8)",
            borderRadius: 8
          }} className="flex items-center justify-center">
            <LoadingSpin />
          </div>
        )}
        
        <div style={{ 
          background: 'white', 
          padding: '40px', 
          borderRadius: 8, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          margin: '0 20px'
        }}>
          <div className="text-center mb-8">
            <h1 style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: '#1890ff',
              marginBottom: '8px'
            }}>
              Tosol Inverter
            </h1>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Hệ thống quản lý inverter năng lượng mặt trời
            </p>
          </div>

          <Tabs activeKey={activeTab} onChange={setActiveTab} centered
            items={[
              {
                key: 'login',
                label: 'Đăng nhập',
                children: (
              <Form
                name="login_form"
                initialValues={{
                  email: "",
                  password: ""
                }}
                onFinish={onLogin}
                autoComplete="off"
                layout="vertical"
                size="large"
              >
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng nhập email!',
                    },
                    {
                      type: 'email',
                      message: 'Email không hợp lệ!',
                    },
                  ]}
                >
                  <Input 
                    prefix={<MailOutlined />} 
                    placeholder="Nhập email của bạn"
                  />
                </Form.Item>

                <Form.Item
                  label="Mật khẩu"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng nhập mật khẩu!',
                    },
                    {
                      min: 6,
                      message: 'Mật khẩu phải có ít nhất 6 ký tự!',
                    },
                  ]}
                >
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder="Nhập mật khẩu"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: '8px' }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    className="w-full"
                    loading={loading}
                    style={{ height: '40px' }}
                  >
                    Đăng nhập
                  </Button>
                </Form.Item>

                <Form.Item style={{ marginBottom: '8px', textAlign: 'right' }}>
                  <Button 
                    type="link" 
                    onClick={() => setForgotPasswordVisible(true)}
                    style={{ padding: 0, fontSize: '14px', height: 'auto' }}
                  >
                    Quên mật khẩu?
                  </Button>
                </Form.Item>

                <Divider style={{ margin: '8px 0' }}>Hoặc</Divider>

                <Form.Item>
                  <Button 
                    type="default"
                    className="w-full"
                    loading={loading}
                    style={{ height: '40px' }}
                    onClick={onLoginWithGoogle}
                    icon={
                      <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: '8px' }}>
                        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                        <path fill="#FBBC05" d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.712s.102-1.172.282-1.712V4.956H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.044l3.007-2.332z"/>
                        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.956L3.964 7.288C4.672 5.163 6.656 3.58 9 3.58z"/>
                      </svg>
                    }
                  >
                    Đăng nhập bằng Google
                  </Button>
                </Form.Item>
              </Form>
                )
              },
              {
                key: 'register',
                label: 'Đăng ký',
                children: (
              <Form
                name="register_form"
                initialValues={{
                  email: "",
                  password: "",
                  confirmPassword: ""
                }}
                onFinish={onRegister}
                autoComplete="off"
                layout="vertical"
                size="large"
              >
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng nhập email!',
                    },
                    {
                      type: 'email',
                      message: 'Email không hợp lệ!',
                    },
                  ]}
                >
                  <Input 
                    prefix={<MailOutlined />} 
                    placeholder="Nhập email của bạn"
                  />
                </Form.Item>

                <Form.Item
                  label="Mật khẩu"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng nhập mật khẩu!',
                    },
                    {
                      min: 6,
                      message: 'Mật khẩu phải có ít nhất 6 ký tự!',
                    },
                  ]}
                >
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder="Nhập mật khẩu"
                  />
                </Form.Item>

                <Form.Item
                  label="Xác nhận mật khẩu"
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng xác nhận mật khẩu!',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                      },
                    }),
                  ]}
                >
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder="Nhập lại mật khẩu"
                  />
                </Form.Item>

                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    className="w-full"
                    loading={loading}
                    style={{ height: '40px' }}
                  >
                    Đăng ký
                  </Button>
                </Form.Item>
              </Form>
                )
              }
            ]}
          />
        </div>
      </div>

      {/* Modal Quên mật khẩu */}
      <Modal
        title="Quên mật khẩu"
        open={forgotPasswordVisible}
        onCancel={() => setForgotPasswordVisible(false)}
        footer={null}
        width={400}
      >
        <Form
          name="forgot_password_form"
          layout="vertical"
          onFinish={async (values) => {
            try {
              setForgotPasswordLoading(true);
              const result = await firebase_api.forgotPassword(values.email);
              
              if (result.success) {
                message.success(result.message || 'Đã gửi link reset mật khẩu đến email của bạn. Vui lòng kiểm tra hộp thư.');
                setForgotPasswordVisible(false);
              }
            } catch (err) {
              console.error('Forgot password error:', err);
              message.error(err.message || 'Gửi email reset mật khẩu thất bại. Vui lòng thử lại!');
            } finally {
              setForgotPasswordLoading(false);
            }
          }}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                message: 'Vui lòng nhập email!',
              },
              {
                type: 'email',
                message: 'Email không hợp lệ!',
              },
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="Nhập email của bạn"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              className="w-full"
              loading={forgotPasswordLoading}
              style={{ height: '40px' }}
            >
              Gửi link reset mật khẩu
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', color: '#666', fontSize: '12px', marginTop: '8px' }}>
            Bạn sẽ nhận được email hướng dẫn reset mật khẩu
          </div>
        </Form>
      </Modal>
    </div>
  );
}

